/** @jsxImportSource @emotion/react */

import { COLORS } from '@btime/constants'
import { css } from '@emotion/react'
import { isEmpty } from 'ramda'
import ExtDayPicker, { DateUtils } from 'react-day-picker'
import React, { useEffect, useRef } from 'react'
import moment from 'moment'

import Separator from '../../helpers/Separator'
import SmallCalendarHeader from './SmallCalendarHeader'

const style = css`
  text-align: center;
  font-family: 'SF Pro Display', 'Nunito', sans-serif;
  max-width: 20rem;
  margin: 0 auto;
  color: ${COLORS.dark2};

  & .DayPicker-wrapper,
  & .DayPicker {
    outline-style: none;
  }

  & .DayPicker-NavButton--next {
    background: red;
    right: 0.6rem;
  }

  & .DayPicker-Week,
  & .DayPicker-WeekdaysRow {
    display: flex;
    align-items: center;
    justify-content: space-around;
  }

  & .DayPicker-Day,
  & .DayPicker-Weekday {
    outline-style: none;
    flex-basis: 14%;
    padding: 0.6rem;
  }

  & .DayPicker-Weekday,
  & .DayPicker-Weekday abbr[title] {
    font-size: 0.6rem;
    color: ${COLORS.dark3};
    text-decoration: none;
    text-transform: uppercase;
    font-weight: 600;
  }

  & .DayPicker-Day {
    cursor: pointer;
  }

  &.range .DayPicker-Day--selected:not(.DayPicker-Day--outside) {
    background: ${COLORS.light1};
    border-radius: 0;
  }
  &:not(.range) .DayPicker-Day--selected:not(.DayPicker-Day--outside),
  &.range .DayPicker-Day--end:not(.DayPicker-Day--outside),
  &.range .DayPicker-Day--start:not(.DayPicker-Day--outside) {
    background: ${COLORS.purple};
    font-weight: 600;
    color: white;
  }

  &:not(.range) .DayPicker-Day--selected:not(.DayPicker-Day--outside) {
    border-radius: 5px;
  }

  &.range .DayPicker-Day--end:not(.DayPicker-Day--outside) {
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
  }
  &.range .DayPicker-Day--start:not(.DayPicker-Day--outside) {
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
  }
`

// TODO: Refactor
const SmallCalendar = ({ useRange, onChange, value, ...props }) => {
  const processInitialValue = () => {
    let processedValue = value

    if (processedValue === undefined || processedValue === null) {
      processedValue = useRange ? [] : undefined
    } else {
      processedValue = !useRange && !!processedValue ? moment(processedValue).toDate() : processedValue
    }

    let defaultValue
    const emptyLocalValue = useRange ? { to: undefined, from: undefined } : undefined

    if (useRange) {
      if (!processedValue || isEmpty(processedValue)) {
        defaultValue = emptyLocalValue
      } else {
        if (Array.isArray(processedValue) && processedValue.length >= 2) {
          let [from, to] = processedValue
          defaultValue = { from, to }
        } else {
          defaultValue = emptyLocalValue
        }
      }
    } else {
      defaultValue = processedValue
    }

    return defaultValue
  }

  const isInitialMount = useRef(true)

  const [localValue, setLocalValue] = React.useState(processInitialValue())
  const [month, setMonth] = React.useState(new Date())

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    const newValue = processInitialValue()

    if (JSON.stringify(newValue) !== JSON.stringify(localValue)) {
      setLocalValue(newValue)

      if (useRange && newValue && newValue.from) {
        setMonth(newValue.from)
      } else if (!useRange && !!newValue) {
        setMonth(newValue)
      }
    }
  }, [value])

  const { from, to } = useRange && localValue ? localValue : { from: undefined, to: undefined }

  const modifiers = useRange ? { start: from, end: to } : undefined
  const selectedDays = useRange ? [from, { from, to }] : localValue

  const handleMonthChange = (n, date) => () => {
    if (!date) date = new Date()
    const newDate = DateUtils.addMonths(date, n)
    setMonth(newDate)
  }

  const handleChange = day => {
    if (!day) return

    if (!useRange) {
      setLocalValue(day)
      onChange(moment(day).startOf('day').toDate())
    } else {
      const emptyLocalValue = { to: undefined, from: undefined }
      const newValue = DateUtils.addDayToRange(day, localValue || emptyLocalValue)
      setLocalValue(newValue)

      if (!!newValue && newValue.from && newValue.to) {
        const formattedFrom = moment(newValue.from).startOf('day').toDate()
        const formattedTo = moment(newValue.to).endOf('day').toDate()
        onChange([formattedFrom, formattedTo])
      }
    }
  }

  return (
    <div>
      <SmallCalendarHeader setMonth={setMonth} month={month} handleMonthChange={handleMonthChange} />
      <ExtDayPicker
        css={style}
        className={useRange ? 'range' : ''}
        month={month}
        onDayClick={handleChange}
        modifiers={modifiers}
        selectedDays={selectedDays}
        captionElement={() => false}
        {...props}
      />

      <Separator weight={4} />
    </div>
  )
}

export default SmallCalendar
